/**
 * AssignmentFeedPanel — 「전체 테스트」 화면(학습 좌측 패널의 표 자리).
 *
 * 카드 chrome·검색칸·페이지 번호줄은 학생 목록(`StudentTablePanel`)의 얼굴을 그대로 쓰고,
 * 표만 `AssignmentFeedTable`(구 테스트관리 8열)로 바꾼다. 같은 자리에서 표만 갈리는 것이
 * 오너가 정한 동작이다(2026-09-06) — 「학생 / 테스트」를 오가는 버튼 쌍은 두지 않는다.
 * 되돌아오는 길은 브라우저 뒤로가기와 사이드바의 「학습」이고, 그래서 이 화면을 주소로 가른다.
 *
 * ■ 데이터
 * `assignmentFeedApi.listAllAssignments()` 하나만 부르고, 그 안은 서버 목록 한 번이다
 * (`GET /api/v2/exam-papers/assignments` · 2026-09-06 신설). 종전에는 그 자리에서 시험지를 돌며
 * 조립했고 400건이 넘으면 표 대신 사유를 적었다 — 서버가 쪽으로 주면서 그 상한이 사라졌다.
 *
 * ■ 검색칸은 함께(AND) 걸린다
 * 구 화면은 칸마다 [검색]이 따로 붙고 한 번에 하나만 서버로 갔다(`ExamList.js` 의 activeFilter).
 * 여기서는 학생 표와 같은 [조회]·[초기화] 한 쌍을 쓰고 조건을 함께 건다 — 서버도 조건을
 * AND 로 받는다(2026-09-06 회신 §1).
 * 담당 강사은 **고르는 칸이 아니라 적는 칸**이다. 구 화면의 목록은 v1(`/api/users/teacher`)에서
 * 오는데 이 화면은 v2 만 쓴다 — v2 에 교사 목록 경로가 **없다고 확인됐다**(회신 §2 4-8).
 * 세 번째 칸은 「학생 이름」이 아니라 **학생·시험지 검색**이다. 서버의 `q` 가 학생명과 시험지 제목을
 * 함께 훑기 때문이고(회신 §1), 칸 이름만 옛것으로 두면 화면이 거짓말을 한다.
 * 글자 칸은 100자에서 막는다 — 서버가 그 위를 400 으로 돌려보내는데, 다 친 뒤에 「잘못된 요청」을
 * 보는 것보다 애초에 못 넘게 하는 편이 낫다(`ASSIGNMENT_FILTER_MAXLEN`).
 */
import { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, RefreshCw, X } from 'lucide-react';
import { Button } from '../common';
import { useAuth } from '../../context/AuthContext';
import { useDesktopExpanded } from '../../hooks/useDesktopExpanded';
import { describeApiError, ASSIGNMENT_FILTER_MAXLEN } from '../ExamPapersV2/examPaperApi';
import { listAllAssignments } from './assignmentFeedApi';
import AssignmentFeedTable from './AssignmentFeedTable';
import '../../styles/desktop-expanded.css';

/** 채점 창 — 학습 화면·시험지 화면이 이미 쓰는 그 창이다. 세 번째 진입이 붙을 뿐이다 */
const AssignmentGradeModal = lazy(() => import('../ExamPapersV2/AssignmentGradeModal'));

const EMPTY = { assignmentId: '', studentId: '', studentName: '', schoolName: '', teacher: '' };

/**
 * 한 쪽에 몇 줄을 놓을지 — 학생 목록과 **같은 자**다(`StudentTablePanel.calculateItemsPerPage`).
 * 두 표가 같은 자리에서 갈리는데 줄 수가 다르면, 오갈 때마다 카드 높이가 들썩인다.
 */
function calcPerPage(isMobileView) {
  try {
    const windowHeight = window.innerHeight;
    const buffer = 10;
    if (isMobileView) return 20;
    const cardHeight = windowHeight - 96;
    const cardInternalFixed = 32 + 28 + 80 + 16 + 40 + 60 + buffer;
    const count = Math.floor((cardHeight - cardInternalFixed) / 48);
    return Math.max(5, Math.min(count, 50));
  } catch (e) {
    return 10;
  }
}

export default function AssignmentFeedPanel() {
  const { getAccessToken } = useAuth();
  const { isMobileView } = useDesktopExpanded();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => calcPerPage(isMobileView));
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  /**
   * 서버가 이 응답에 **적용한 범위**('all' 학원 전체 · 'mine' 담당 학생만).
   * 지금은 어느 토큰으로 불러도 `'all'` 이 온다 — 서버가 범위를 좁히지 않기로 했고(2026-09-06 회신
   * §2 4-2, 오너 결정), 좁혀야 하면 화면이 담당 교사 이름이나 학생 번호로 건다.
   * 그 사실을 화면이 말해 두지 않으면, 권한을 일괄로 손보는 날 **그동안 무엇이 열려 있었는지**
   * 아무도 모른다. 서버가 `'mine'` 을 주기 시작하면 글자만 바뀌고 이 화면은 그대로 선다.
   */
  const [scope, setScope] = useState(null);

  // 입력창의 라이브 값과 [조회]로 확정된 값을 가른다 — 페이지를 넘길 때 확정값으로 다시 부른다
  const [form, setForm] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);

  // 늦게 도착한 옛 응답이 새 화면에 앉지 않게 하는 자물쇠
  const seqRef = useRef(0);
  /**
   * 창 크기 감시가 **마운트당 한 번만** 등록되도록, 그때 읽어야 할 최신 값들을 ref 로 든다
   * (학생 목록과 같은 자 — `StudentTablePanel` 의 resizeFetchRef).
   * 의존성에 넣어 매 렌더마다 다시 등록하면 조립이 도는 중에도 리스너가 갈아 끼워진다.
   */
  const perPageRef = useRef(perPage);
  const appliedRef = useRef(applied);
  const isMobileRef = useRef(isMobileView);

  const load = useCallback(async (nextPage, filters, size) => {
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const r = await listAllAssignments({ page: nextPage, perPage: size, filters }, getAccessToken);
      if (seq !== seqRef.current) return;
      setRows(r.items);
      setPage(r.page);
      setTotalItems(r.totalItems);
      setTotalPages(r.totalPages);
      setScope(r.scope ?? null);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setRows([]);
      setTotalItems(0);
      setTotalPages(1);
      setScope(null);
      setError(describeApiError(e, '테스트 목록을 불러오지 못했습니다.'));
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    load(1, EMPTY, perPage);
    // 첫 진입에서 한 번만 — 이후는 조회·페이지·새로고침이 부른다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  perPageRef.current = perPage;
  appliedRef.current = applied;
  isMobileRef.current = isMobileView;

  /**
   * 창 크기가 바뀌면 줄 수를 다시 재고 첫 쪽부터 다시 그린다.
   * **200ms 를 기다린다** — 학생 목록이 쓰는 것과 같은 자다(`StudentTablePanel:266`).
   * 창을 끄는 동안 resize 는 초당 수십 번 오는데, 디바운스가 없으면 끄는 내내 목록을 다시 부른다.
   * 왕복이 하나로 줄었어도 마찬가지다 — 한 번이 싸진 것이지 수십 번이 싸진 것은 아니다.
   * 줄 수가 그대로면 아무것도 하지 않는다 — 세로로만 조금 늘려도 다시 부르지 않기 위해서다.
   */
  useEffect(() => {
    let timer = null;
    const onResize = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const next = calcPerPage(isMobileRef.current);
        if (next === perPageRef.current) return;
        setPerPage(next);
        load(1, appliedRef.current, next);
      }, 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
    // 마운트당 1회만 등록한다 — 그때 읽어야 할 값은 전부 ref 로 들었다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSearch = () => { setApplied(form); load(1, form, perPage); };
  const onReset = () => { setForm(EMPTY); setApplied(EMPTY); load(1, EMPTY, perPage); };
  const onKeyDown = (e) => { if (e.key === 'Enter') onSearch(); };
  const goPage = (p) => { if (p >= 1 && p <= totalPages && p !== page) load(p, applied, perPage); };

  /**
   * 지금 채점 중인 배정 — **주소가 진실이다**(`?grade=<배정 번호>`).
   * 형제 화면 둘이 이미 같은 이름을 쓴다(학습 화면 `?grade=`, 시험지 화면 `?tab=assign&grade=`).
   * 주소는 남이 손으로 고칠 수 있어 정수가 아니면 열지 않는다.
   */
  const gradeParam = searchParams.get('grade');
  const gradingId = /^\d+$/.test(String(gradeParam ?? '')) ? gradeParam : null;

  const openGrading = useCallback((aid) => {
    if (aid == null) return;
    const next = new URLSearchParams(location.search);
    next.set('grade', String(aid));
    // 히스토리에 쌓는다(replace 가 아니다) — 그래야 뒤로가기가 목록이 아니라 창만 닫는다.
    // 「내가 열었다」는 표식을 그 칸에 실어 둔다: ref 로 들면 앞으로가기로 되살아난 창을 닫을 때
    // 「내가 연 것이 아니다」로 읽혀 앞으로 갈 자리가 사라진다(학습 화면과 같은 자).
    navigate(
      { pathname: location.pathname, search: `?${next.toString()}` },
      { state: { ...(location.state || {}), feedGradingPushed: true } },
    );
  }, [location, navigate]);

  const closeGrading = useCallback(() => {
    if (location.state?.feedGradingPushed) { navigate(-1); return; }
    const next = new URLSearchParams(location.search);
    next.delete('grade');
    const search = next.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [location, navigate]);

  /**
   * 창에서 채점·마감하면 이 표의 정답·오답·상태·일시가 낡는다. 창은 표의 자식이 아니라 이
   * 패널이 띄우므로, 닫힐 때 지금 쪽을 다시 읽는다.
   */
  const onGraded = useCallback(() => { load(page, applied, perPage); }, [load, page, applied, perPage]);

  const shown = rows.length;
  const pageNumbers = useMemo(() => {
    const max = 5;
    let start = Math.max(1, page - Math.floor(max / 2));
    const end = Math.min(start + max - 1, totalPages);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    const out = [];
    for (let i = start; i <= end; i += 1) out.push(i);
    return out;
  }, [page, totalPages]);

  const narrow = isMobileView;
  const inputCls = `${narrow ? 'w-full' : 'flex-1'} min-w-0 rounded border px-2 py-1 text-xs`;

  // 서버가 범위를 안 실어 준 응답(옛 서버·오류)에서는 아무것도 적지 않는다 — 지어내지 않는다
  const scopeText = scope === 'all' ? '학원 전체' : (scope === 'mine' ? '담당 학생만' : null);

  return (
    <div className="flex size-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      {/* 헤더 — 학생 목록과 같은 자리·같은 여백 */}
      <div className="shrink-0 border-b" style={{ padding: narrow ? '12px' : '16px' }}>
        <div className="mb-2 flex items-center gap-3">
          <h3 className="text-lg font-semibold">테스트 목록</h3>
          <p className="text-xs text-gray-500">
            {/* 「v2 시험지 배정」이라고 적는다 — 구 테스트관리 화면(17,726개)과 얼굴이 같아
                수가 갑자기 작아진 것을 고장으로 읽기 때문이다 */}
            - v2 시험지 배정 <span className="font-semibold text-primary">{totalItems || 0}</span>건 중{' '}
            <span className="font-semibold text-primary">{shown}</span>건 표시 중
          </p>
          {scopeText && (
            <span
              className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500"
              title={scope === 'all'
                ? '이 목록은 학원 전체의 배정입니다 — 서버가 계정에 따라 범위를 좁히지 않습니다'
                : '이 목록은 담당 학생의 배정만입니다'}
            >
              {scopeText}
            </span>
          )}
          {/* 오른쪽 끝의 한 묶음 — [새로고침][닫기]. 줄의 gap-3 은 제목·설명·배지를 가르는 자라,
              한 벌로 읽혀야 할 두 버튼 사이만 gap-1 로 좁힌다(채점 창 머리줄이 쓰는 것과 같은 자). */}
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => load(page, applied, perPage)}
              disabled={loading}
              title="새로고침"
              aria-label="테스트 목록 새로고침"
              className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
            >
              <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {/* 나가는 길을 화면 안에 둔다 — 종전에는 브라우저 뒤로가기와 사이드바의 「학습」뿐이었다
                (오너 지시 2026-09-07). 가는 곳은 이 화면에 들어온 자리, 곧 학생 목록(/students-hub)이다.
                뒤로가기(navigate(-1))를 쓰지 않는 이유: 이 화면은 주소를 몇 번 갈아입는다(채점 창을
                여닫을 때마다 ?grade= 가 히스토리에 쌓인다). 한 칸 뒤가 학습 화면이라는 보장이 없다.

                얼굴은 **학습 화면의 [닫기]**(StudentLearningPanel:249)를 값 그대로 옮긴 것이다
                (오너 지시 2026-09-07). 이 저장소에서 「이 화면을 닫는다」는 버튼은 그 하나뿐이라,
                같은 일을 하는 자리가 다른 얼굴로 서면 두 화면이 같은 제품으로 읽히지 않는다.
                90px 고정 너비까지 같은 값이다 — 좁은 화면에서는 레퍼런스와 같이 글자를 내리고
                아이콘만 남긴다. 아이콘만 남는 그때를 위해 aria-label 을 둔다(얼굴은 바뀌지 않는다).
                아이콘 크기만 레퍼런스의 `w-4 h-4` 대신 `size-4` 로 적는다 — 그려지는 크기는 16px 로
                같고, 이 저장소의 린트가 축약형을 요구한다(tailwindcss/enforces-shorthand). */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/students-hub')}
              title="닫기 — 학습(학생 목록)으로 돌아갑니다"
              aria-label="테스트 목록 닫기"
              className="flex shrink-0 items-center justify-center gap-1"
              style={{ width: narrow ? undefined : '90px' }}
            >
              <X className="size-4" />
              {!narrow && '닫기'}
            </Button>
          </div>
        </div>

        <div className="mt-3 flex gap-2">
          <div className={narrow ? 'grid flex-1 grid-cols-2 gap-2' : 'flex flex-1 gap-2'}>
            <input type="text" name="assignmentId" placeholder="배정 번호" value={form.assignmentId} onChange={onField} onKeyDown={onKeyDown} maxLength={ASSIGNMENT_FILTER_MAXLEN} className={inputCls} />
            <input type="text" name="studentId" placeholder="학생 ID" value={form.studentId} onChange={onField} onKeyDown={onKeyDown} maxLength={ASSIGNMENT_FILTER_MAXLEN} className={inputCls} />
            <input type="text" name="studentName" placeholder="학생·시험지 검색" title="학생 이름 또는 시험지 제목의 일부" value={form.studentName} onChange={onField} onKeyDown={onKeyDown} maxLength={ASSIGNMENT_FILTER_MAXLEN} className={inputCls} />
            <input type="text" name="schoolName" placeholder="학교 이름" value={form.schoolName} onChange={onField} onKeyDown={onKeyDown} maxLength={ASSIGNMENT_FILTER_MAXLEN} className={inputCls} />
            <input type="text" name="teacher" placeholder="담당 강사" value={form.teacher} onChange={onField} onKeyDown={onKeyDown} maxLength={ASSIGNMENT_FILTER_MAXLEN} className={inputCls} />
          </div>
          <div className={narrow ? 'flex w-16 flex-col gap-2' : 'flex gap-2'}>
            <Button onClick={onReset} variant="secondary" size="sm" rounded="3xl" className={`${narrow ? 'flex-1' : 'px-6'} min-h-[22px]`}>초기화</Button>
            <Button onClick={onSearch} variant="primary" size="sm" rounded="3xl" className={`${narrow ? 'flex-1' : 'px-6'} min-h-[22px]`}>조회</Button>
          </div>
        </div>
      </div>

      {/* 컨텐츠 */}
      <div className="flex flex-1 flex-col overflow-y-auto" style={{ marginTop: '8px', minHeight: 0 }}>
        {loading && rows.length === 0 ? (
          <div className="py-4 text-center text-sm text-gray-500">불러오는 중...</div>
        ) : error ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm font-medium text-danger">{error}</p>
            <Button onClick={() => load(page, applied, perPage)} variant="secondary" size="sm" rounded="3xl" className="mt-3 px-6">다시 시도</Button>
          </div>
        ) : (
          <div className="min-w-0 flex-1 overflow-x-auto" style={{ minHeight: 0, width: '100%' }}>
            <AssignmentFeedTable rows={rows} onOpen={openGrading} tight={narrow} />
          </div>
        )}

        {/* 페이지 번호줄 — 학생 목록과 같은 얼굴 */}
        {!loading && !error && rows.length > 0 && (
          <div className="shrink-0 bg-white pb-4">
            <div className={narrow ? 'flex items-center justify-center gap-2' : 'desktop-pagination'}>
              <button
                className={narrow ? 'flex size-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50' : 'page-button'}
                style={!narrow ? { borderRadius: '1.5rem' } : {}}
                onClick={() => goPage(page - 1)}
                disabled={page === 1}
                aria-label="이전 페이지"
              >
                {narrow ? '<' : <ChevronLeft size={16} className="font-bold" />}
              </button>
              <div className="flex gap-1">
                {pageNumbers.map((p) => (
                  <button
                    key={p}
                    onClick={() => goPage(p)}
                    className={`${narrow ? 'h-8 min-w-8 rounded-full border px-2 text-xs' : 'page-button'} ${p === page ? (narrow ? 'border-primary bg-primary font-bold text-white' : 'active font-bold') : (narrow ? 'border-gray-300 bg-white text-gray-600' : '')}`}
                    style={!narrow ? { borderRadius: '1.5rem' } : {}}
                    aria-label={`${p} 페이지`}
                    aria-current={p === page ? 'page' : undefined}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <button
                className={narrow ? 'flex size-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50' : 'page-button'}
                style={!narrow ? { borderRadius: '1.5rem' } : {}}
                onClick={() => goPage(page + 1)}
                disabled={page >= totalPages}
                aria-label="다음 페이지"
              >
                {narrow ? '>' : <ChevronRight size={16} className="font-bold" />}
              </button>
            </div>
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="py-8 text-center">
            <p className="text-sm font-medium text-gray-500">출제된 테스트가 없습니다</p>
            <p className="mt-1 text-xs text-gray-400">검색 조건을 바꾸거나 초기화해 보세요</p>
          </div>
        )}
      </div>

      {/* 채점 창 — 목록을 배경에 두고 뜬다. 닫으면 하던 자리로 돌아온다 */}
      {gradingId != null && (
        <Suspense fallback={null}>
          <AssignmentGradeModal
            key={`g-${gradingId}`}
            assignmentId={gradingId}
            isOpen
            onClose={closeGrading}
            onChanged={onGraded}
          />
        </Suspense>
      )}
    </div>
  );
}
